import { Alert } from '../lib/db';

interface AlertCardProps {
  alert: Alert;
  onAcknowledge?: (alertId: string) => void;
}

export default function AlertCard({ alert, onAcknowledge }: AlertCardProps) {
  const severityConfig =
    alert.severity === 'critical' ? {
      bg: 'bg-red-50',
      border: 'border-alertCritical',
      badgeClass: 'badge-critical',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      ),
      pulse: 'animate-pulse'
    } :
    alert.severity === 'high' ? {
      bg: 'bg-orange-50',
      border: 'border-alertHigh',
      badgeClass: 'badge-high',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      ),
      pulse: ''
    } :
    alert.severity === 'medium' ? {
      bg: 'bg-yellow-50',
      border: 'border-alertMedium',
      badgeClass: 'badge-medium',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      ),
      pulse: ''
    } : {
      bg: 'bg-peacockTeal-50',
      border: 'border-peacockTeal-500',
      badgeClass: 'badge-low',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      ),
      pulse: ''
    };

  const syncStatusIcon = alert.syncStatus === 'pending' ? (
    <span className="text-yellow-600 animate-pulse" title="Pending sync">
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
      </svg>
    </span>
  ) : alert.syncStatus === 'error' ? (
    <span className="text-red-600" title="Sync error">
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
    </span>
  ) : null;

  return (
    <article
      className={`alert-card ${severityConfig.bg} border-2 ${severityConfig.border} ${severityConfig.pulse}`}
      role="alert"
      aria-live={alert.severity === 'critical' ? 'assertive' : 'polite'}
      aria-label={`${alert.severity} alert for patient ${alert.patient}: ${alert.title}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Severity Badge */}
          <div className="flex items-center gap-2 mb-3">
            <span className={severityConfig.badgeClass} aria-label={`Alert severity: ${alert.severity}`}>
              <span aria-hidden="true">{severityConfig.icon}</span>
              {alert.severity || 'Alert'}
            </span>
            {syncStatusIcon}
          </div>

          {/* Alert Title */}
          <h4 className="text-lg font-bold text-gray-900 mb-2">
            {alert.title}
          </h4>

          {/* Patient Info */}
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <p className="text-sm font-semibold text-gray-700">
              Patient: <span className="text-peacockTeal-600">{alert.patient}</span>
            </p>
          </div>

          {/* Timestamp */}
          {alert.timestamp && (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <time dateTime={new Date(alert.timestamp).toISOString()}>
                {new Date(alert.timestamp).toLocaleString()}
              </time>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="ml-4 flex flex-col items-end gap-2">
          {onAcknowledge && !alert.acknowledged && (
            <button
              onClick={() => onAcknowledge(alert.id)}
              className="btn btn-primary"
              aria-label={`Acknowledge ${alert.severity} alert for ${alert.patient}`}
            >
              <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Acknowledge
            </button>
          )}
          {alert.acknowledged && (
            <div
              className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg shadow-md flex items-center gap-2"
              role="status"
              aria-label="Alert has been acknowledged"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-bold">Acknowledged</span>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
